// src/oura/components/connectorLogos.tsx
//
// Brand marks for the connector cards: the real logos (or faithful geometric
// recreations), drawn in brand colours on a white tile — the treatment
// integration directories use. The Google G is the canonical four-colour
// mark; GA4 is the official bar-and-dot chart; the rest are close inline
// recreations to be swapped for design's official SVG assets at handoff.

import type { ConnectorKey } from "../connectors";
import { cn } from "../lib/cn";

function Glyph({ k }: { k: ConnectorKey }) {
    switch (k) {
        case "gsc":
            // The canonical four-colour Google "G".
            return (
                <>
                    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
                    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
                    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z" />
                </>
            );
        case "ga4":
            // The official Analytics bar-and-dot chart.
            return (
                <>
                    <rect x="15.2" y="2.5" width="5.6" height="19" rx="2.8" fill="#F9AB00" />
                    <rect x="8.2" y="9.5" width="5.6" height="12" rx="2.8" fill="#E37400" />
                    <circle cx="4" cy="18.7" r="2.8" fill="#E37400" />
                </>
            );
        case "bing":
            return (
                <text x="12" y="13" dominantBaseline="central" textAnchor="middle" fontSize="19" fontWeight={800} fill="#008373" fontFamily="system-ui, sans-serif">
                    b
                </text>
            );
        case "wordpress":
            return (
                <>
                    <circle cx="12" cy="12" r="11" fill="#21759B" />
                    <text x="12" y="12.6" dominantBaseline="central" textAnchor="middle" fontSize="12" fontWeight={700} fill="#fff" fontFamily="Georgia, serif">
                        W
                    </text>
                </>
            );
        case "framer":
            // Framer's mark: two stacked bands folding into a triangle.
            return (
                <g fill="#0055FF">
                    <path d="M4 0h16v8h-8z" />
                    <path d="M4 8h8l8 8H4z" />
                    <path d="M4 16h8v8z" />
                </g>
            );
        case "webflow":
            return (
                <text x="12" y="12.5" dominantBaseline="central" textAnchor="middle" fontSize="17" fontWeight={800} fontStyle="italic" fill="#146EF5" fontFamily="system-ui, sans-serif">
                    W
                </text>
            );
        case "wix":
            return (
                <text x="12" y="12.5" dominantBaseline="central" textAnchor="middle" fontSize="9" fontWeight={800} letterSpacing="0.5" fill="#0C0C0C" fontFamily="system-ui, sans-serif">
                    WIX
                </text>
            );
        case "shopify":
            // Shopping bag in Shopify greens with the S.
            return (
                <>
                    <path d="M8.2 7.2V6a3.8 3.8 0 0 1 7.6 0v1.2" fill="none" stroke="#5E8E3E" strokeWidth="1.8" strokeLinecap="round" />
                    <rect x="4.2" y="7.2" width="15.6" height="14" rx="2.4" fill="#95BF47" />
                    <text x="12" y="14.6" dominantBaseline="central" textAnchor="middle" fontSize="10" fontWeight={800} fill="#fff" fontFamily="system-ui, sans-serif">
                        S
                    </text>
                </>
            );
        case "squarespace":
            // The interlocking-link mark.
            return (
                <g fill="none" stroke="#1A1A1A" strokeWidth="2.6" strokeLinecap="round">
                    <path d="M6.2 13.6l5.4-5.4a3.2 3.2 0 0 1 4.5 4.5l-.9.9" />
                    <path d="M17.8 10.4l-5.4 5.4a3.2 3.2 0 0 1-4.5-4.5l.9-.9" />
                </g>
            );
        case "gbp":
            // The Business Profile storefront: scalloped awning over the shop.
            return (
                <>
                    <rect x="5" y="10.5" width="14" height="9.5" rx="1.4" fill="#669DF6" />
                    <path
                        fill="#4285F4"
                        d="M4 4h16l1.4 4.6a2.6 2.6 0 0 1-5.2.3 2.6 2.6 0 0 1-5.2 0 2.6 2.6 0 0 1-5.2 0A2.6 2.6 0 0 1 2.6 8.6z"
                    />
                    <rect x="13.4" y="13.4" width="3.6" height="6.6" rx="0.8" fill="#fff" />
                </>
            );
        case "google_docs":
            // The Docs page: a blue sheet with its top corner folded, a
            // lighter fold with a shadow under it, and four white text lines.
            return (
                <>
                    <path fill="#4285F4" d="M14.5 1H5.75A1.75 1.75 0 0 0 4 2.75v18.5A1.75 1.75 0 0 0 5.75 23h12.5A1.75 1.75 0 0 0 20 21.25V6.5z" />
                    <path fill="#3367D6" d="M15.75 6.5 20 10.75V6.5z" />
                    <path fill="#A1C2FA" d="M14.5 1v4.25c0 .69.56 1.25 1.25 1.25H20z" />
                    <g fill="#fff">
                        <rect x="7.5" y="10.6" width="9" height="1.4" rx="0.5" />
                        <rect x="7.5" y="13.1" width="9" height="1.4" rx="0.5" />
                        <rect x="7.5" y="15.6" width="9" height="1.4" rx="0.5" />
                        <rect x="7.5" y="18.1" width="6" height="1.4" rx="0.5" />
                    </g>
                </>
            );
    }
    // Every ConnectorKey above returns, so `k` narrows to `never` here: add a key
    // to the union without a mark and this call stops compiling.
    assertEveryKeyHasAMark(k);
    // Still reachable at RUNTIME, because adaptConnectors() hands us a
    // BE-supplied string and the backend catalog can grow before this file does.
    // A letter is not a logo, but it is a connector you can name — which is all
    // the blank Framer tile ever needed to be.
    return (
        <text
            x="12" y="13" dominantBaseline="central" textAnchor="middle"
            fontSize="13" fontWeight={700} fill="#6B7280"
            fontFamily="system-ui, sans-serif"
        >
            {String(k).charAt(0).toUpperCase()}
        </text>
    );
}

/** Compile-time only: the argument is `never` when the switch above is total. */
function assertEveryKeyHasAMark(k: never): void {
    void k;
}

export function ConnectorLogo({ k, className = "h-9 w-9" }: { k: ConnectorKey; className?: string }) {
    return (
        <span
            className={cn(
                "flex flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white",
                className,
            )}
        >
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                <Glyph k={k} />
            </svg>
        </span>
    );
}
